import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container } from 'reactstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MenuRightIcon from 'mdi-react/MenuRightIcon';
import { getGcmMerchants } from '@/services/program/getGcmMerchants';
import { STATIC_CATEGORIES, CategoryTab, categoryPath } from '@/containers/Participant/GcmShared/categories';
import { YelpRating } from '@/containers/Participant/GcmShared/yelp';
import { GcmSearchBar } from '@/containers/Participant/GcmShared/GcmHeader';
import MerchantPhoto from '@/containers/Participant/GcmShared/MerchantPhoto';
import { openBusiness, yelpBusinessUrl } from '@/containers/Participant/GcmShared/helpers';
import useGcmLocation, { LOCATION_SOURCE } from '@/containers/Participant/GcmShared/useGcmLocation';
import './index.scss';

const PAGE_SIZE = 10;

// The search API has no sort parameter, so sorting is applied to the loaded results
const SORTS = [
    { label: 'Best Match' },
    { label: 'Highest Rated', compare: (a, b) => (b.rating || 0) - (a.rating || 0) },
    { label: 'Most Reviewed', compare: (a, b) => (b.review_count || 0) - (a.review_count || 0) },
];

const GcmSearchResults = () => {
    const navigate = useNavigate();
    // The URL is the source of truth for the applied search, so results are shareable
    // and back/forward work; the input only becomes the query on Apply
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('q') || '';

    const geo = useGcmLocation(searchParams.get('loc'));
    const { location } = geo;
    const [searchInput, setSearchInput] = useState(query);
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortIndex, setSortIndex] = useState(0);
    const [page, setPage] = useState(0);
    const resultsRef = useRef(null);

    useEffect(() => {
        if (!location) return undefined;
        let cancelled = false;
        setLoading(true);
        getGcmMerchants({ location, search: query })
            .then(data => {
                if (cancelled) return;
                setBusinesses(data.businesses || []);
                setPage(0);
            })
            .catch(console.error)
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [query, location]);

    const applySearch = (nextQuery) => {
        const params = { loc: location };
        if (nextQuery) params.q = nextQuery;
        setSearchParams(params);
    };

    // Keep ?loc= in step when the location changes on this page (typed or device)
    useEffect(() => {
        if (location && searchParams.get('loc') !== location) {
            const params = { loc: location };
            if (query) params.q = query;
            setSearchParams(params, { replace: true });
        }
    }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReset = () => {
        setSearchInput('');
        applySearch('');
    };

    const sort = SORTS[sortIndex];
    const sorted = useMemo(
        () => (sort.compare ? [...businesses].sort(sort.compare) : businesses),
        [businesses, sort]
    );
    const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
    const pageStart = page * PAGE_SIZE;
    const pageItems = sorted.slice(pageStart, pageStart + PAGE_SIZE);

    const goToPage = (next) => {
        setPage(next);
        // Smooth scrolling (the site's `scroll-behavior: smooth`) never completes on this
        // layout, so jump instantly; the offset keeps the heading clear of the top bar
        const el = resultsRef.current;
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'instant' });
    };

    const cycleSort = () => {
        setSortIndex(i => (i + 1) % SORTS.length);
        setPage(0);
    };

    return (
        <div className="gsr-page">
            <Container>
                <GcmSearchBar
                    value={searchInput}
                    onChange={setSearchInput}
                    onApply={() => applySearch(searchInput.trim())}
                    onReset={handleReset}
                    geo={geo}
                    brand
                />
            </Container>

            {/* Category tabs on a tinted band */}
            <div className="gsr-band">
                <Container>
                    <div className="gsr-categories">
                        {STATIC_CATEGORIES.map(cat => (
                            <CategoryTab
                                key={cat.id}
                                category={cat}
                                className="gsr-category"
                                iconSize={72}
                                multiline
                                onClick={() => navigate(categoryPath(cat))}
                            />
                        ))}
                    </div>
                </Container>
            </div>

            <Container>
                {!loading && businesses.length > 0 && (
                    <div className="gsr-results-header" ref={resultsRef}>
                        <div>
                            <h3 className="gsr-results-header__title">
                                {geo.source === LOCATION_SOURCE.DEVICE ? 'Results near you' : `Results in ${location}`}
                            </h3>
                            <p className="gsr-results-header__count">
                                SHOWING {pageStart + 1}–{pageStart + pageItems.length} OF {sorted.length}
                            </p>
                        </div>
                        <button type="button" className="gsr-sort" onClick={cycleSort} title="Change sort order">
                            Sort: {sort.label} <MenuRightIcon size={16} />
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="gsr-loading">
                        <div className="spinner-border spinner-border-sm text-warning" role="status" />
                        <span>&nbsp;Loading...</span>
                    </div>
                ) : businesses.length === 0 ? (
                    <p className="gsr-empty">No results found. Try a different search.</p>
                ) : (
                    <>
                        <ol className="gsr-list" start={pageStart + 1}>
                            {pageItems.map((b, i) => {
                                const address = b.location?.display_address?.join(', ')
                                    || [b.location?.city, b.location?.state].filter(Boolean).join(', ');
                                return (
                                    <li key={b.id} className="gsr-item">
                                        <div className="gsr-item__img">
                                            <MerchantPhoto url={b.image_url} alt={b.name} placeholderClassName="gsr-item__img--placeholder" eager />
                                        </div>
                                        <div className="gsr-item__info">
                                            <h4 className="gsr-item__name" title={b.name}>{pageStart + i + 1}. {b.name}</h4>
                                            {b.rating > 0 && (
                                                <YelpRating
                                                    className="gsr-item__rating"
                                                    rating={b.rating}
                                                    reviewCount={b.review_count}
                                                    href={yelpBusinessUrl(b)}
                                                    bracketed
                                                />
                                            )}
                                            {address && <p className="gsr-item__address" title={address}>{address}</p>}
                                            <button className="gsr-item__select" onClick={() => openBusiness(navigate, b)}>
                                                Select
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>

                        {pageCount > 1 && (
                            <div className="gsr-pager">
                                <button type="button" className="gsr-pager__btn" disabled={page === 0} onClick={() => goToPage(page - 1)}>
                                    Previous
                                </button>
                                <span className="gsr-pager__status">Page {page + 1} of {pageCount}</span>
                                <button type="button" className="gsr-pager__btn" disabled={page >= pageCount - 1} onClick={() => goToPage(page + 1)}>
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </Container>
        </div>
    );
};

export default GcmSearchResults;
